import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Upload, Trash2, Star } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import { useCommercialArchitectureDesign } from "../architecture-design/useHook";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";
import { sanitizePositionInput } from "../../../Utils/positionInput";

const CommercialArchitectureDesignDetails = () => {
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission(SystemPermissions.CommercialArchitectureDesign.UPDATE);
  const {
    getCommercialArchitectureDesignById,
    updateCommercialArchitectureDesign,
    updateCommercialArchitectureDesignPrimaryImage,
    showCommercialArchitectureDesignOnHomeScreen,
  } = useCommercialArchitectureDesign();
  const [houseData, setHouseData] = useState(null);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("description");
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHomeScreenLoading, setIsHomeScreenLoading] = useState(false);
  const [primaryImageId, setPrimaryImageId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    plot_dimension: "",
    covered_area: "",
    floors: 0,
    customUrl: "",
    description: "",
    servant_quarters: false,
    terrace: false,
    car_parking: false,
    images: [],
    // price: 0,
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImagePositions, setNewImagePositions] = useState([]);

  useEffect(() => {
    if (id) {
      getCommercialArchitectureDesignById(id, setHouseData);
    }
  }, [id]);

  useEffect(() => {
    if (houseData) {
      setFormData({
        title: houseData.title || "",
        plot_dimension: houseData.plot_dimension || "",
        covered_area: houseData.covered_area || "",
        floors: houseData.floors || 0,
        customUrl: houseData.customUrl || "",
        description: houseData.description || "",
        servant_quarters: houseData.servant_quarters || false,
        terrace: houseData.terrace || false,
        car_parking: houseData.car_parking || false,
        images: [],
        // price: houseData.price || 0,
      });
      setExistingImages(
        (houseData.images || [])
          .map((img, index) => ({
            ...img,
            position: img.is_primary
              ? ""
              : img.position != null && Number(img.position) >= 1
                ? img.position
                : index + 1,
          }))
          .sort((a, b) => {
            if (!!a.is_primary !== !!b.is_primary) return a.is_primary ? -1 : 1;
            return (Number(a.position) || 0) - (Number(b.position) || 0);
          }),
      );
      setNewImagePositions([]);
      const primary = (houseData.images || []).find(img => img.is_primary);
      if (primary) {
        setPrimaryImageId(primary.id);
      }
    }
  }, [houseData, id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleQuillChange = (value) => {
    setFormData({
      ...formData,
      description: value,
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    const maxExistingPosition = existingImages.reduce((max, img) => {
      if (img.is_primary) return max;
      const n = Number(img.position);
      return Number.isInteger(n) && n >= 1 ? Math.max(max, n) : max;
    }, 0);
    const maxNewPosition = newImagePositions.reduce((max, pos) => {
      const n = Number(pos);
      return Number.isInteger(n) && n >= 1 ? Math.max(max, n) : max;
    }, maxExistingPosition);

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));

    setImagePreviews((prev) => [...prev, ...newPreviews]);
    setNewImagePositions((prev) => [
      ...prev,
      ...files.map((_, i) => maxNewPosition + i + 1),
    ]);
  };

  const removeImage = (index, isExisting = false) => {
    if (isExisting) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newImages = [...formData.images];
      const newPreviews = [...imagePreviews];
      const positions = [...newImagePositions];

      URL.revokeObjectURL(newPreviews[index]);
      newImages.splice(index, 1);
      newPreviews.splice(index, 1);
      positions.splice(index, 1);

      setFormData((prev) => ({
        ...prev,
        images: newImages,
      }));
      setImagePreviews(newPreviews);
      setNewImagePositions(positions);
    }
  };

  const onExistingImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onNewImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setNewImagePositions((prev) => {
      const next = [...prev];
      next[index] = cleaned;
      return next;
    });
  };

  const handleSetPrimary = async (imageId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to change the primary image of this commercial architecture design?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!",
    });

    if (result.isConfirmed) {
      try {
        await updateCommercialArchitectureDesignPrimaryImage(imageId);
        setPrimaryImageId(imageId);
        setExistingImages((prev) => {
          let nextIndex = 1;
          return prev.map((img) => {
            if (img.id === imageId) {
              return { ...img, is_primary: true, position: "" };
            }
            return {
              ...img,
              is_primary: false,
              position: nextIndex++,
            };
          });
        });
        // Also refresh the main data to stay in sync
        await getCommercialArchitectureDesignById(id, setHouseData);

        Swal.fire({
          title: "Success",
          text: "Primary image has been updated",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Failed to update primary image:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to update primary image. Please try again.",
          icon: "error",
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate custom URL
    if (!formData.customUrl || formData.customUrl.trim() === "") {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "images") {
          apiData.append(key, formData[key]);
        }
      });

      if (primaryImageId) {
        apiData.append("primary_image_id", primaryImageId);
      }

      // Add new images
      formData.images.forEach((image, index) => {
        apiData.append("images", image);
        apiData.append(
          "imagePosition",
          newImagePositions[index] != null ? newImagePositions[index] : index + 1,
        );
      });

      // Add existing images that weren't removed
      existingImages.forEach((image) => {
        apiData.append("existing_images", JSON.stringify(image));
      });

      await updateCommercialArchitectureDesign(id, apiData);
      await getCommercialArchitectureDesignById(id, setHouseData);
      setShowEditModal(false);

      // Cleanup image previews
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
      setImagePreviews([]);

      Swal.fire({
        icon: "success",
        title: "Commercial Architecture Design Updated",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to update commercial architecture design:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update commercial architecture design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHomeScreenItem = async () => {
    if (!houseData?.id) return;

    setIsHomeScreenLoading(true);
    const newStatus = !houseData.homeScreen;

    try {
      await showCommercialArchitectureDesignOnHomeScreen(houseData.id, newStatus ? 1 : 0);
      await getCommercialArchitectureDesignById(houseData.id, setHouseData);
      Swal.fire({
        title: "Success",
        text: newStatus
          ? "Commercial architecture design has been added to home screen"
          : "Commercial architecture design has been removed from home screen",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update home screen status:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update home screen status. Please try again.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setIsHomeScreenLoading(false);
    }
  };

  // Cleanup image previews on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, []);

  if (!houseData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-xl font-semibold">Loading house details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-100 p-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-1">Commercial Architecture Design Details</h2>
        </div>
        <div className="flex space-x-4">
          {/* {canUpdate && (
            <button
              type="button"
              onClick={() => setShowEditModal(true)}
              className="btn btn-edit text-white"
            >
              Edit
            </button>
          )} */}
        </div>
      </div>

      <div className="max-w-6xl mx-auto my-6 p-6 bg-white shadow-lg rounded-lg">
        <div className="mb-6">
          <h2 className="text-3xl font-bold">{houseData.title}</h2>
          <p className="text-gray-600 mt-1">Design ID: {houseData.designId}</p>
          <p className="text-sm text-gray-500 mt-2">
            Created: {new Date(houseData.created_at).toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="mb-2">
              <span className="font-semibold">Plot Dimension: </span>
              {houseData.plot_dimension}
            </p>
            <p className="mb-2">
              <span className="font-semibold">Covered Area: </span>
              {houseData.covered_area}
            </p>
            <p className="mb-2">
              <span className="font-semibold">Floors: </span>
              {houseData.floors}
            </p>
          </div>

          <div>
            <p className="mb-2">
              <span className="font-semibold">Car Parking: </span>
              {houseData.car_parking ? "Yes" : "No"}
            </p>
            <p className="mb-2">
              <span className="font-semibold">Terrace: </span>
              {houseData.terrace ? "Yes" : "No"}
            </p>
            <p className="mb-2">
              <span className="font-semibold">Servant Quarters: </span>
              {houseData.servant_quarters ? "Yes" : "No"}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex border-b border-gray-300 mb-4">
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${
                activeTab === "description"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${
                activeTab === "images"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("images")}
            >
              Images
            </button>
          </div>

          {activeTab === "description" && (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: houseData.description }}
            />
          )}

          {activeTab === "images" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {houseData.images?.map((img) => (
                <img
                  key={img.id}
                  src={img.image_url}
                  alt="House"
                  className="rounded shadow-md object-cover"
                />
              ))}
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-1/2 max-h-[90vh] flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold">Edit Commercial Architecture Design</h2>
              </div>
              <div className="overflow-y-auto flex-1">
                <form onSubmit={handleSubmit} className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-2"
                        required
                      />
                    </div>
                    {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div> */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plot Dimension
                      </label>
                      <input
                        type="text"
                        name="plot_dimension"
                        value={formData.plot_dimension}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Covered Area
                      </label>
                      <input
                        type="text"
                        name="covered_area"
                        value={formData.covered_area}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Floors
                      </label>
                      <input
                        type="number"
                        name="floors"
                        value={formData.floors}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Url <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="customUrl"
                        value={formData.customUrl}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md p-2"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <ReactQuill
                      value={formData.description}
                      onChange={handleQuillChange}
                      className="h-48 mb-12"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="car_parking"
                        checked={formData.car_parking}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Car Parking
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="terrace"
                        checked={formData.terrace}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Terrace
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="servant_quarters"
                        checked={formData.servant_quarters}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Servant Quarters
                      </label>
                    </div>
                  </div>

                  {/* Image Upload Section */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Images
                    </label>

                    {/* Existing Images */}
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        Current Images
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {existingImages.map((image, index) => (
                          <div key={image.id} className="relative group">
                            <div className="relative">
                              <img
                                src={image.image_url}
                                alt={`House ${index + 1}`}
                                className={`w-full h-32 object-cover rounded-lg transition-all ${
                                  image.is_primary ? "ring-4 ring-blue-500 shadow-lg" : ""
                                }`}
                              />
                              {image.is_primary && (
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm whitespace-nowrap">
                                  PRIMARY
                                </div>
                              )}
                            </div>
                            <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => handleSetPrimary(image.id)}
                                className={`p-1 rounded-full transition-colors ${
                                  image.is_primary
                                    ? "bg-blue-500 text-white"
                                    : "bg-white text-gray-600 hover:bg-blue-100"
                                } shadow-md`}
                                title={image.is_primary ? "Primary Image" : "Set as Primary"}
                              >
                                <Star
                                  size={16}
                                  fill={image.is_primary ? "currentColor" : "none"}
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeImage(index, true)}
                                className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-md"
                                title="Remove Image"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                            {!image.is_primary ? (
                              <div className="mt-2">
                                <label className="block text-xs text-gray-600 mb-1">
                                  Position
                                </label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[1-9][0-9]*"
                                  value={image.position ?? ""}
                                  onChange={(e) =>
                                    onExistingImagePositionChange(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  placeholder="1"
                                />
                              </div>
                            ) : (
                              <p className="mt-2 text-xs text-blue-600 text-center font-medium">
                                Primary (not indexed)
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* New Images Preview */}
                    {imagePreviews.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                          New Images
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {imagePreviews.map((preview, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={preview}
                                alt={`New image ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 size={16} />
                              </button>
                              <div className="mt-2">
                                <label className="block text-xs text-gray-600 mb-1">
                                  Position
                                </label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[1-9][0-9]*"
                                  value={newImagePositions[index] ?? ""}
                                  onChange={(e) =>
                                    onNewImagePositionChange(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  placeholder="1"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Image Upload Button */}
                    <div className="mt-4">
                      <label className="flex items-center justify-center w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
                        <Upload size={20} className="mr-2" />
                        <span>Add New Images</span>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 bg-blue-500 text-white rounded-md flex items-center ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        "Update"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CommercialArchitectureDesignDetails;
